import React, { useId } from 'react'
import { Box, Typography } from '@mui/material'

// The same calm ambers as BillingWarning and AgreementReminder. Amber here
// means "AmbuNet will look at this with you", never "something has failed":
// no red and no error icon are used anywhere in this component.
const AMBER_FILL = '#F59E0B'
const AMBER_TEXT = '#92400E'
const AMBER_TRACK = 'rgba(245, 158, 11, 0.2)'

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value)

const formatFigure = (value) =>
  Number(value).toLocaleString('en-GB', { maximumFractionDigits: 1 })

// One allowance as a line: what it is, how much of it is used, and a bar.
//
// The three variants are three different kinds of allowance, and they must
// not look alike when they are full:
//
//   hard  a capacity limit (employees, vehicles, base locations, concurrently
//         open control rooms). Using all of it is normal, so a full bar is
//         only drawn in a stronger colour and labelled "At capacity".
//   soft  an allowance recorded for review only (PTS journeys, control-room
//         hours). Nothing is ever blocked by one. Above it, the line turns
//         calm amber and reads "Above allowance".
//   info  usage that is not being reviewed against an allowance. It has no
//         "above" state at all, whatever the figures are.
//
//   label            what is being measured
//   used             how much is used
//   allowance        the allowance, or null / undefined when there is none:
//                    "No limit" for a hard line, otherwise "Not included in
//                    your plan", with an empty bar
//   unit             optional plural word after "<used> of <allowance>"
//                    ("journeys", "hours")
//   aboveAllowance   soft only. Pass the server's own verdict where there is
//                    one; left out, it is worked out from the figures
//   usedLabel        optional replacement for the "<used> of <allowance>" text
//   caption          optional small print under the figures
const AllowanceProgressLine = ({
  label,
  used = 0,
  allowance,
  unit,
  variant = 'hard',
  aboveAllowance,
  usedLabel,
  caption,
}) => {
  const labelId = useId()
  const usedValue = isNumber(used) ? used : 0
  const hasAllowance = isNumber(allowance) && allowance > 0
  const unitText = unit ? ` ${unit}` : ''

  const isAbove =
    variant === 'soft' &&
    hasAllowance &&
    (aboveAllowance ?? usedValue > allowance)
  const isFull = variant === 'hard' && hasAllowance && usedValue >= allowance

  const status = isAbove
    ? 'Above allowance'
    : isFull
      ? usedValue > allowance
        ? 'Above capacity'
        : 'At capacity'
      : null

  const fillColour = isAbove
    ? AMBER_FILL
    : isFull
      ? 'primary.main'
      : variant === 'info'
        ? 'secondary.light'
        : 'contrast.main'
  const statusColour = isAbove ? AMBER_TEXT : 'text.primary'

  // With nothing to measure against, the label alone names what the figure
  // counts (and "1 journeys" is avoided).
  const figures =
    usedLabel ??
    (hasAllowance
      ? `${formatFigure(usedValue)} of ${formatFigure(allowance)}${unitText}`
      : variant === 'hard'
        ? `${formatFigure(usedValue)} · No limit`
        : usedValue > 0
          ? `${formatFigure(usedValue)} · Not included in your plan`
          : 'Not included in your plan')

  const percent = hasAllowance
    ? Math.max(0, Math.min(100, (usedValue / allowance) * 100))
    : 0

  const trackSx = {
    mt: 0.5,
    height: 10,
    borderRadius: 5,
    overflow: 'hidden',
    bgcolor: isAbove ? AMBER_TRACK : 'greycontrast.main',
  }

  // Label, bar, then the figures: whatever wraps is below the bar, so bars
  // set side by side always sit on the same line.
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography id={labelId} variant="body1" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>

      {hasAllowance ? (
        <Box
          role="progressbar"
          aria-labelledby={labelId}
          aria-valuemin={0}
          aria-valuemax={allowance}
          aria-valuenow={Math.max(0, Math.min(usedValue, allowance))}
          aria-valuetext={`${figures}${status ? `, ${status.toLowerCase()}` : ''}`}
          sx={trackSx}
        >
          <Box
            sx={{
              width: `${percent}%`,
              height: '100%',
              borderRadius: 5,
              bgcolor: fillColour,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
      ) : (
        // Nothing to measure against, so there is no progress to announce:
        // the empty track is decoration and the text below says it all.
        <Box aria-hidden sx={trackSx} />
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {figures}
        {status && (
          <Box component="span" sx={{ color: statusColour, fontWeight: 600 }}>
            {' '}
            · {status}
          </Box>
        )}
      </Typography>

      {caption && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  )
}

export default AllowanceProgressLine
